import dayjs from "dayjs";

import styles from "../../../styles/order-card.module.scss";

import { Button } from "../../components/Button";
import { MapMarkerIcon } from "../../components/Icons";

import ROLES from "data/Roles";
import { useEffect, useState } from "react";
import { UserStorage } from "shared/tools/UserStorage";
import { Icon } from "../../components/core/Icon";
import { AccessManager, Permissions } from "../../tools/AccessManager";
import { convertMoney } from "../../utils";
import { BrazilStates } from "../../utils/BrazilStates";

const locale_ptbr = require("dayjs/locale/pt-br");
dayjs.locale(locale_ptbr);

const DeliveryType = {
  DELIVER_ON_ADDRESS: 1,
  GET_ON_PLACE: 2,
  INDOOR: 3,
};

const OrderCard = ({
  order,
  selectOrder,
  onChangeStatus,
  allowManualDispatch,
  onDispatch,
  orderGoing,
}) => {
  const [going, setGoing] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [loading, setLoading] = useState(false);

  const OrderStatus = {
    IN_PRODUCTION: "IN_PRODUCTION",
    ON_DELIVERY: "ON_DELIVERY",
    FINISHED: "FINISHED",
    CANCELED: "CANCELED",
    REJECTED: "REJECTED",
  };

  const OrderOrigin = {
    ONLINE: "ONLINE",
    TOTEM: "TOTEM",
  };

  const openOrderDetails = (e, order) => {
    e.stopPropagation();
    selectOrder(order);
  };

  useEffect(() => {
    setArriving(orderGoing === order.id);

    setGoing(false);
  }, [orderGoing, order.id]);

  const handleChangeStatus = (order, orderStatus) => {
    setGoing(true);
    onChangeStatus(order, orderStatus);
  };

  const handleDispatch = async () => {
    if (onDispatch) {
      setLoading(true);
      setTimeout(() => setLoading(false), 5000);
      await onDispatch();
    }
  };

  const generateAddressString = (deliveryAddress) => {
    if (!deliveryAddress || !deliveryAddress.address) {
      return "Erro no endereço";
    }

    let stringAddress = `${deliveryAddress.address}`;

    if (deliveryAddress.number) {
      stringAddress += `, ${deliveryAddress.number}`;
    }

    if (deliveryAddress.complement) {
      stringAddress += ` (${deliveryAddress.complement})`;
    }

    const shortNameState = BrazilStates.getShortNameByName(
      deliveryAddress.state
    );

    stringAddress += ` - ${deliveryAddress.region} - ${deliveryAddress.city}/${shortNameState}`;
    return stringAddress;
  };

  const OrderCardButton = () => {
    const typesOfDeliveries = {
      IFOOD_DELIVERY: "iFood",
      MOTTU: "Mottu",
      FOODY_DELIVERY: "Foody Delivery",
      BEE_DELIVERY: "Bee Delivery",
    };

    const deliveryType = order?.delivery?.deliveryPlatformShipping;
    const deliveryName = typesOfDeliveries[deliveryType];

    let userRole = UserStorage.getLoggedUser()?.role;
    if (!userRole) userRole = ROLES.SHOPKEEPER;

    if (order.status === OrderStatus.IN_PRODUCTION) {
      if (deliveryType) {
        return (
          <Button design="default-4" className="w-100" disabled>
            <i>
              Aguardando entregador&nbsp;
              <strong>{deliveryName || deliveryType}</strong>
            </i>
          </Button>
        );
      }

      if (
        order?.delivery?.type === DeliveryType.DELIVER_ON_ADDRESS &&
        !deliveryType &&
        allowManualDispatch
      ) {
        return (
          <Button
            design="default-2"
            className="w-100"
            loading={loading}
            disabled={loading}
            onClick={handleDispatch}
          >
            <i>Solicitar entregador</i>
          </Button>
        );
      }

      return (
        <Button
          design="default-2"
          className="w-100"
          disabled={
            !AccessManager.hasPermissions([Permissions.ORDERS_DISPATCH])
          }
          onClick={() =>
            onChangeStatus && handleChangeStatus(order, OrderStatus.ON_DELIVERY)
          }
        >
          {order.delivery?.type != DeliveryType.DELIVER_ON_ADDRESS
            ? "Pronto para retirada"
            : "Saiu para entrega"}
        </Button>
      );
    }

    if (order.status === OrderStatus.ON_DELIVERY) {
      if (deliveryType) {
        return (
          <Button design="default-4" className="w-100" disabled>
            <i>
              Entregando via&nbsp;
              <strong>{deliveryName || deliveryType}</strong>
            </i>
          </Button>
        );
      }

      return (
        <Button
          design="default-2"
          className="w-100"
          disabled={!AccessManager.hasPermissions([Permissions.ORDERS_FINISH])}
          onClick={() =>
            onChangeStatus && handleChangeStatus(order, OrderStatus.FINISHED)
          }
        >
          Finalizar pedido
        </Button>
      );
    }

    if ([OrderStatus.CANCELED, OrderStatus.REJECTED].includes(order.status)) {
      return (
        <Button design="danger" className="w-100" disabled>
          Pedido{" "}
          {order.status === OrderStatus.REJECTED ? "rejeitado" : "cancelado"}
        </Button>
      );
    }

    if ([OrderStatus.FINISHED].includes(order.status)) {
      return (
        <>
          <div className={styles.hairline} />
          <p className={styles.orderFinished}>Pedido finalizado</p>
        </>
      );
    }
  };

  const getOrderOrigin = (origin) => {
    const origins = {
      [OrderOrigin.ONLINE]: "App",
      [OrderOrigin.TOTEM]: "Totem",
    };

    return origins[origin];
  };

  const getOrderWarning = () => {
    const orderStatuses = (order?.statuses || []).filter(
      (orderStatus) => orderStatus?.value?.reason
    );

    if (orderStatuses.length === 0) {
      return null;
    }

    const orderErrors = orderStatuses.filter((orderStatus) =>
      [OrderStatus.CANCELED, OrderStatus.REJECTED].includes(orderStatus.status)
    );

    return {
      icon: "exclamation-circle",
      color: orderErrors.length > 0 ? "#D85A60" : "#FFF1AD",
    };
  };

  const hasNoDeliveryReason = () => {
    const orderStatuses = order?.statuses || [];

    return orderStatuses.some(
      (orderStatus) =>
        ![OrderStatus.CANCELED, OrderStatus.REJECTED].includes(
          orderStatus.status
        ) && orderStatus?.value?.reason
    );
  };

  const orderWarning = getOrderWarning();

  return (
    <div
      className={`${styles.orderCard} ${going ? styles.toRight : ""} ${
        arriving ? styles.fromLeft : ""
      }`}
      onClick={(e) => openOrderDetails(e, order)}
    >
      <div className={styles.header}>
        <p className={styles.customerName}>
          {order.customer_infos ? order.customer_infos.name : ""}
          {order.position && <span>&nbsp;- Senha {order.position}</span>}
        </p>
        <div className="d-flex align-center">
          {orderWarning && (
            <div className="d-flex align-center mr-15">
              <Icon
                name={orderWarning.icon}
                light
                size="1x"
                color={orderWarning.color}
              />
            </div>
          )}
          {order.delivery.scheduledFor && (
            <div className={styles.icon}>
              <Icon name="calendar" light size="lg" />
            </div>
          )}
          <div className={styles.icon}>{getOrderOrigin(order.origin)}</div>
        </div>
      </div>

      <div className={styles.content}>
        <div className="d-flex justify-between">
          <div className="d-flex flex-column">
            <p className={styles.total}>Valor total</p>
            <span className={styles.value}>{convertMoney(order.total)}</span>
          </div>
          <div className="d-flex flex-column">
            <p className={styles.number}>
              Olga <span>#{order.displayId}</span>
              {order?.delivery?.shipping && (
                <>
                  &nbsp;/ iFood{" "}
                  <span>#{order.delivery.shipping?.displayId}</span> / Coleta{" "}
                  <span>#{order.delivery.shipping?.collectCode}</span>
                </>
              )}
            </p>

            <span className={styles.date}>
              Chegou {dayjs(order.created_at).fromNow()}
            </span>
          </div>
        </div>

        <div className={styles.delivery}>
          <p>Local</p>
          <div className="d-flex align-center justify-between">
            <div className="d-flex align-center">
              <MapMarkerIcon />
              <span>
                {order.delivery.type != DeliveryType.GET_ON_PLACE
                  ? generateAddressString(order.delivery.address)
                  : "Retirada no balcão"}
              </span>
            </div>
            {order.delivery.type == DeliveryType.GET_ON_PLACE && (
              <p className={styles.withdrawalType}>Para viagem</p>
            )}
            {order.delivery.type == DeliveryType.INDOOR && (
              <p className={styles.withdrawalType}>Consumo no local</p>
            )}
          </div>
        </div>

        <OrderCardButton />
      </div>
    </div>
  );
};

export default OrderCard;
